import React, { useEffect, useState } from "react";
import { X, BellRing, CheckCircle2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { newEventId } from "../lib/track";
import { journey, visitorId } from "../lib/journey";
import EmailHint from "./EmailHint";

export default function NotifyModal({ open, item, onClose, trackEvent }) {
    const [form, setForm] = useState({ name: "", email: "", whatsapp: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [again, setAgain] = useState(false);
    const key = item ? `${item.kind}:${item.slug}` : "";
    // 4 Nov 2026 (RK): count who opens "Notify me" per item, so Admin -> Demand shows interest, not just sign-ups.
    useEffect(() => { if (open && key) journey("notify_open", key); }, [open, key]);
    if (!open || !item) return null;
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const eid = newEventId();
            const r = await api.post("/notify", { kind: item.kind, slug: item.slug, ...form, event_id: eid, vid: visitorId() });
            setAgain(/already/i.test(r.data?.message || ""));
            journey("notify_submit", key);
            trackEvent?.("Lead", { type: "waitlist", slug: item.slug }, { eventID: eid });
            setDone(true);
        } catch (err) { alert(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const close = () => { setDone(false); setForm({ name: "", email: "", whatsapp: "" }); onClose(); };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="notify-modal">
            <div className="absolute inset-0 bg-espresso/60 backdrop-blur-sm" onClick={close} />
            <div className="relative w-full max-w-md bg-white shadow-2xl">
                <button onClick={close} className="absolute top-4 right-4 text-neutral-400 hover:text-ink" data-testid="notify-close"><X className="h-5 w-5" /></button>
                {done ? (
                    <div className="p-9 text-center">
                        <CheckCircle2 className="h-12 w-12 text-brand-gold mx-auto" />
                        <h3 className="mt-4 font-display font-semibold text-2xl">{again ? "You're already on the list" : "You're on the list"}</h3>
                        <p className="mt-2 text-neutral-500">We'll email (and WhatsApp you, if provided) the moment <b>{item.title}</b> goes live.</p>
                        <button onClick={close} className="btn-primary mt-6 w-full">Done</button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-7 sm:p-9" data-testid="notify-form">
                        <div className="flex items-center gap-2 text-brand-golddark"><BellRing className="h-5 w-5" /><p className="overline !mb-0">Coming soon</p></div>
                        <h3 className="mt-3 font-display font-semibold text-2xl leading-tight">Get notified when<br /><span className="italic text-brand-gold">{item.title}</span> launches</h3>
                        <p className="mt-2 text-sm text-neutral-500">Be first to know. No spam, just the launch announcement.</p>
                        <div className="mt-5 space-y-3">
                            <input required placeholder="Your name" value={form.name} onChange={set("name")} data-testid="notify-name" className="w-full border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink" />
                            <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} data-testid="notify-email" className="w-full border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink" />
                            <EmailHint email={form.email} onUse={(fixed) => setForm({ ...form, email: fixed })} />
                            <input placeholder="WhatsApp number (optional)" value={form.whatsapp} onChange={set("whatsapp")} data-testid="notify-whatsapp" className="w-full border border-[#d9cfbc] px-4 py-3 outline-none focus:border-ink" />
                        </div>
                        <button disabled={busy} className="btn-primary w-full mt-5 disabled:opacity-60" data-testid="notify-submit">{busy ? "Adding…" : "Notify Me"}</button>
                    </form>
                )}
            </div>
        </div>
    );
}
